'use client'

import { useTranslations } from 'next-intl'
import { type ReactNode, useMemo, useState } from 'react'

import { SegmentedControl } from '@/components/ui/segmented-control'
import { PlanCard, PlanCardCta } from '@/features/billing/components/plan-card'
import { maxYearlySavingsPercent } from '@/features/billing/lib/format'
import { PlanLevel } from '@/types/generated/common/account'
import { Capabilities, Plan } from '@/types/generated/public_api'

const INTERVAL_ORDER = ['month', 'year']

function sortIntervals(intervals: string[]): string[] {
  return [...intervals].sort((a, b) => {
    const ia = INTERVAL_ORDER.indexOf(a)
    const ib = INTERVAL_ORDER.indexOf(b)
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
  })
}

interface PlansGridProps {
  plans: Plan[]
  /** Signed-in viewer's capabilities; omit on the public pricing page. */
  userCapabilities?: Capabilities
  /** Viewer can still claim their one-time free trial — drives the trial badge/CTA. */
  trialEligible?: boolean
  /** How each plan's CTA behaves (current plan / checkout / link to sign-up). */
  cta: (plan: Plan) => PlanCardCta
  /** Page heading shown left of the interval toggle, on one row (the orderx.com layout). */
  header?: ReactNode
  /** Compact extra shown at the right end of the toggle row (e.g. the viewer's current plan). */
  aside?: ReactNode
}

/** Interval toggle + the plan-card grid. Shared by the billing view and the public pricing page. */
export function PlansGrid({
  plans,
  userCapabilities,
  trialEligible,
  cta,
  header,
  aside,
}: PlansGridProps) {
  const t = useTranslations('billing')
  const intervals = useMemo(() => {
    const set = new Set<string>()
    plans.forEach((p) => p.prices.forEach((price) => set.add(price.interval)))
    return sortIntervals([...set])
  }, [plans])

  const [selectedInterval, setSelectedInterval] = useState<string | null>(null)
  const interval = selectedInterval ?? intervals[0] ?? 'month'
  // Surface the annual discount on the yearly toggle to nudge the cheaper choice.
  const yearlySavings = useMemo(() => maxYearlySavingsPercent(plans), [plans])
  const intervalLabel = (iv: string) =>
    iv === 'year' ? t('yearly') : iv === 'month' ? t('monthly') : iv

  // Phone: the three cards stacked into a long scroll where reaching Pro meant scrolling past
  // all of Essential's features. Below md one plan shows at a time, picked here, opening on
  // the recommended Pro. From md up every card shows side by side as before.
  const [mobilePlanLevel, setMobilePlanLevel] = useState<PlanLevel | null>(null)
  const activeMobileLevel =
    mobilePlanLevel ??
    plans.find((p) => p.level === PlanLevel.PLAN_LEVEL_PRO)?.level ??
    plans[0]?.level

  return (
    <div className="flex flex-col gap-6 md:gap-8">
      {header || aside || intervals.length > 1 ? (
        <div className="flex flex-wrap items-end justify-between gap-6">
          {header}
          {intervals.length > 1 ? (
            <SegmentedControl
              size="md"
              className="w-fit"
              value={interval}
              onChange={setSelectedInterval}
              options={intervals.map((iv) => ({
                value: iv,
                label: intervalLabel(iv),
                badge: iv === 'year' && yearlySavings !== null ? `-${yearlySavings}%` : undefined,
              }))}
            />
          ) : null}
          {aside ? <div className="ml-auto self-center">{aside}</div> : null}
        </div>
      ) : null}

      {plans.length > 1 && (
        <SegmentedControl
          size="md"
          className="w-full md:hidden"
          value={String(activeMobileLevel)}
          onChange={(v) => setMobilePlanLevel(Number(v) as PlanLevel)}
          options={plans.map((p) => ({ value: String(p.level), label: p.label }))}
        />
      )}

      {/* One translucent panel split by hairline dividers (the orderx.com joined-card
          layout), so the page backdrop shows through the cells. From md up, each card
          spans the three rows as a subgrid so header / price / features line up across
          cards even when one wraps (e.g. a struck-through promo price). */}
      <div className="bg-foreground/5 border-foreground/10 divide-foreground/10 grid items-stretch divide-y border md:auto-cols-fr md:grid-flow-col md:grid-rows-[auto_auto_1fr] md:divide-x md:divide-y-0">
        {plans.map((plan) => (
          <PlanCard
            key={plan.level}
            plan={plan}
            plans={plans}
            interval={interval}
            userCapabilities={userCapabilities}
            trialEligible={trialEligible}
            highlighted={plan.level === PlanLevel.PLAN_LEVEL_PRO}
            cta={cta(plan)}
            hiddenOnMobile={plan.level !== activeMobileLevel}
          />
        ))}
      </div>
    </div>
  )
}
